import { useEffect, useMemo, useState } from 'react'
import { loadClimateIntelligence, type ClimateIntelligence } from '../../lib/dailyContext'

const PERIODS = [7, 15, 30] as const

function formatDay(value: string) {
  const [year, month, day] = value.slice(0, 10).split('-')
  if (!year || !month || !day) return '—'
  return `${day}/${month}`
}

function scoreLabel(score: number) {
  if (score >= 4.5) return 'Muito bem'
  if (score >= 3.5) return 'Bem'
  if (score >= 2.5) return 'Neutro'
  if (score >= 1.5) return 'Não muito bem'
  return 'Muito mal'
}

export function ClimateIntelligencePanel() {
  const [days, setDays] = useState<number>(30)
  const [data, setData] = useState<ClimateIntelligence | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let mounted = true
    setLoading(true)
    setError('')
    loadClimateIntelligence(days)
      .then((result) => mounted && setData(result))
      .catch((err) => mounted && setError(err instanceof Error ? err.message : 'Não foi possível carregar o Clima Operacional.'))
      .finally(() => mounted && setLoading(false))
    return () => { mounted = false }
  }, [days])

  const distribution = useMemo(() => {
    if (!data) return []
    return [
      { score: 1, emoji: '😞', label: 'Muito mal', value: data.distribution.score_1 },
      { score: 2, emoji: '😟', label: 'Não muito bem', value: data.distribution.score_2 },
      { score: 3, emoji: '😐', label: 'Neutro', value: data.distribution.score_3 },
      { score: 4, emoji: '🙂', label: 'Bem', value: data.distribution.score_4 },
      { score: 5, emoji: '😄', label: 'Muito bem', value: data.distribution.score_5 },
    ]
  }, [data])

  const maxDistribution = Math.max(1, ...distribution.map((item) => item.value))

  return (
    <section className="usage-intelligence climate-intelligence">
      <div className="admin-section-title usage-heading">
        <div>
          <p className="eyebrow">CLIMA PRESENCIAL</p>
          <h2>Clima das unidades de atendimento</h2>
          <p className="muted">Pesquisa diária exclusiva das seis unidades presenciais. Call Center e Outros não participam deste indicador.</p>
        </div>
        <div className="usage-periods" aria-label="Período da análise de clima">
          {PERIODS.map((period) => <button key={period} className={days === period ? 'active' : ''} onClick={() => setDays(period)}>{period} dias</button>)}
        </div>
      </div>

      {loading && <div className="empty-state">Carregando clima operacional...</div>}
      {error && <div className="notice error">{error}</div>}

      {!loading && data && (
        <>
          <div className="usage-kpi-grid">
            <article><span>Respostas hoje</span><strong>{data.summary.responses_today}</strong><small>check-ins das lojas presenciais</small></article>
            <article><span>Respostas no período</span><strong>{data.summary.responses_period}</strong><small>{days} dias selecionados</small></article>
            <article><span>Sentimento médio</span><strong>{Number(data.summary.average_score).toLocaleString('pt-BR')}</strong><small>{scoreLabel(Number(data.summary.average_score))} · escala de 1 a 5</small></article>
            <article><span>Clima positivo</span><strong>{Number(data.summary.positive_pct).toLocaleString('pt-BR')}%</strong><small>{Number(data.summary.negative_pct).toLocaleString('pt-BR')}% negativo</small></article>
          </div>

          <div className="usage-grid-two climate-main-grid">
            <section className="usage-panel">
              <div className="usage-panel-head"><div><p className="eyebrow">UNIDADES</p><h3>Sentimento por loja</h3></div><span>{data.units.length} com respostas</span></div>
              <div className="usage-ranking-list climate-unit-ranking">
                {data.units.length === 0 && <div className="empty-state">Ainda não há respostas de clima no período.</div>}
                {data.units.map((item, index) => (
                  <article key={item.unit_key}>
                    <b className="usage-rank">{index + 1}</b>
                    <div>
                      <strong>{item.unit_label}</strong>
                      <small>{item.responses} respostas · {Number(item.positive_pct).toLocaleString('pt-BR')}% positivas · {Number(item.negative_pct).toLocaleString('pt-BR')}% negativas</small>
                    </div>
                    <em>{Number(item.average_score).toLocaleString('pt-BR')}</em>
                  </article>
                ))}
              </div>
            </section>

            <section className="usage-panel">
              <div className="usage-panel-head"><div><p className="eyebrow">DISTRIBUIÇÃO</p><h3>Como as pessoas responderam</h3></div><span>{data.summary.responses_period} respostas</span></div>
              <div className="climate-distribution">
                {distribution.map((item) => (
                  <div key={item.score}>
                    <span className="climate-emoji" aria-hidden="true">{item.emoji}</span>
                    <div><strong>{item.label}</strong><span className="usage-bar"><i style={{ width: item.value > 0 ? `${Math.max(5, item.value / maxDistribution * 100)}%` : '0%' }} /></span></div>
                    <b>{item.value}</b>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <section className="usage-panel">
            <div className="usage-panel-head"><div><p className="eyebrow">EVOLUÇÃO</p><h3>Sentimento médio por dia</h3></div><span>hoje primeiro</span></div>
            <div className="usage-daily-chart climate-daily-chart">
              {data.daily.map((item) => (
                <div className="usage-chart-row climate-chart-row" key={item.date}>
                  <span>{formatDay(item.date)}</span>
                  <span className="usage-bar"><i style={{ width: item.average_score > 0 ? `${item.average_score / 5 * 100}%` : '0%' }} /></span>
                  <strong>{item.average_score ? Number(item.average_score).toLocaleString('pt-BR') : '—'}</strong>
                  <small>{item.responses} respostas</small>
                </div>
              ))}
            </div>
          </section>

          <div className="notice info">O clima é apresentado de forma agregada por unidade e período. O painel não cria ranking individual de sentimento.</div>
        </>
      )}
    </section>
  )
}
